import {SpanIcon} from '@mazraeapp/react_icon_kit';
import {Form, Formik} from 'formik';
import React, {useCallback, useRef, useState} from 'react';
import {useQueryClient} from 'react-query';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate, useParams} from 'react-router-dom';
import {BottomSheet} from 'react-spring-bottom-sheet';
import EmptyImage from '../../../assets/image/shared/chat_empty_profile_image.svg';
import DotsLoading from '../../../components/dotsLoading';
import FormikInput from '../../../components/formikFileds/input';
import FormikSelect from '../../../components/formikFileds/select';
import CustomButton from '../../../components/mazraeKit/customButton';
import CustomHeader from '../../../components/mazraeKit/headers/homeHeader';
import {useAuth} from '../../../context/authContext';
import useDeleteImageProfile from '../../../services/hookService/deleteImageProfile/useDeleteImageProfile';
import useGetProfile from '../../../services/hookService/my-account/getProfile/useGetProfile';
import useUpdateUserProfile from '../../../services/hookService/my-account/updateUserPofile/useUpdateUserProfile';
import {RootState} from '../../../store/redux';
import {addingImage, clearAllData} from '../../../store/redux/editProfile';
import {
  CitiesToOption,
  ProvincesToOption,
} from '../../../utilities/helper/provincesToOption';
import UseGetProvince from '../../../utilities/hooks/province/useGetProvince';
import {AddSchema} from './utils/helper';

const EditMyAccountWrapper = () => {
  const inputFileRef = useRef<HTMLInputElement>(null);
  const [isOpenBottomSheet, setIsOpenBottomSheet] = useState(false);
  const [deleteImageBottomSheet, setdeleteImageBottomSheet] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {guid} = useParams();
  const {provinces} = UseGetProvince();

  const {refetch} = useGetProfile(guid);

  const {user, isSupplier} = useAuth();
  const {cropedImage} = useSelector(
    (state: RootState) => state.editImageProfile,
  );

  const queryClient = useQueryClient();

  const {UpLoadUpdateisLoading, editProfile} = useUpdateUserProfile();
  const {mutate} = useDeleteImageProfile({
    profile_type: 'supplier',
    successAction: () => {
      refetch();
      setdeleteImageBottomSheet(false);
    },
  });

  const profile_image = isSupplier
    ? user?.profile?.supplier_profile?.profile_image
    : user?.profile?.farmer_profile?.profile_image;

  const name = isSupplier
    ? user?.profile?.supplier_profile?.name
    : user?.profile?.farmer_profile?.name;

  const phone_number = isSupplier
    ? user?.profile?.supplier_profile?.phone_number
    : user?.profile?.farmer_profile?.cell_phone;

  const id = isSupplier
    ? user?.profile?.supplier_profile?.guid
    : user?.profile?.farmer_profile?.guid;

  const state = isSupplier
    ? user?.profile?.supplier_profile?.state
    : user?.profile?.farmer_profile?.user_state;

  const city = isSupplier
    ? user?.profile?.supplier_profile?.city
    : user?.profile?.farmer_profile?.user_city;

  const email = isSupplier
    ? user?.profile?.supplier_profile?.email_address
    : user?.profile?.farmer_profile?.email_address;

  const findDefaultCityById = useCallback(
    (value: string) => {
      const province = provinces?.find(state => state.id === +value);
      return province?.cities?.[0]?.id;
    },
    [provinces],
  );

  return (
    <div className="flex flex-col justify-between w-full h-full ">
      <CustomHeader
        title={'پروفایل'}
        preIcon="icon-nameArrow---Right"
        backRoute="/my-account"
        isActiveRightIcon
      />
      <div className="flex flex-col justify-between w-full h-full px-3 py-3 overflow-y-auto">
        {UpLoadUpdateisLoading ? (
          <DotsLoading />
        ) : (
          <div className="flex flex-col items-center justify-between w-full h-full gap-3">
            <div
              className="relative d-flex"
              onClick={() =>
                profile_image || cropedImage
                  ? setIsOpenBottomSheet(true)
                  : inputFileRef.current?.click()
              }
            >
              <div className="w-[32px] h-[32px] absolute bottom-[5px] rounded-full bg-farm-shop-500 flex items-center justify-center">
                {profile_image || cropedImage ? (
                  <div className="flex items-center gap-1 text-md">
                    <SpanIcon className="text-white icon-nameCamera text-xxl" />
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center gap-1 text-md">
                      <SpanIcon className="text-white icon-nameCamera text-xxl" />
                    </div>
                    <input
                      ref={inputFileRef}
                      onChange={event => {
                        event?.target?.files &&
                          dispatch(
                            addingImage({
                              imageFile: event,
                            }),
                          );
                        navigate('/edit/my-account/add-picture');
                      }}
                      className="hidden"
                      type="file"
                      id="avatar"
                      name="avatar"
                      accept="image/png, image/jpeg"
                    />
                  </div>
                )}
              </div>

              <img
                width={84}
                height={84}
                className="rounded-full w-[84px] h-[84px] object-cover"
                src={
                  cropedImage
                    ? URL.createObjectURL(cropedImage)
                    : profile_image
                    ? profile_image
                    : EmptyImage
                }
                alt={'empty-profile'}
              />
            </div>
            <Formik
              initialValues={{
                name: name || '',
                phone_number: phone_number || '',
                state: state?.toString() || '',
                city: city?.toString() || '',
                email_address: email || '',
              }}
              enableReinitialize
              validationSchema={AddSchema}
              onSubmit={values => {
                const payload: IUpdateUserProfilePayload = {
                  city: values.city || '',
                  state: values.state || '',
                  guid: id || '',
                  name: values?.name || '',
                  ...(values?.email_address !== '' && {
                    email_address: values.email_address,
                  }),

                  ...(cropedImage && {
                    profile_image: cropedImage,
                  }),
                };
                editProfile(payload, {
                  onSuccess: () => {
                    refetch();
                    navigate('/my-account');
                    queryClient.invalidateQueries({
                      queryKey: ['user-profile'],
                    });
                  },
                });
              }}
            >
              {({values, setFieldValue}) => (
                <Form className="flex flex-col items-center justify-between w-full h-full">
                  <div className="flex flex-col items-center w-full gap-4">
                    <FormikInput
                      name="name"
                      label="نام و نام خانوادگی"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setFieldValue('name', e.target.value);
                      }}
                      required={!values.name}
                    />
                    <FormikInput
                      disabled
                      name="phone_number"
                      label="شماره همراه"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setFieldValue('phone_number', e.target.value);
                      }}
                      required={!values.phone_number}
                    />
                    <FormikInput
                      name="email_address"
                      label="ایمیل"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setFieldValue('email_address', e.target.value);
                      }}
                      required={!values.name}
                    />
                    <FormikSelect
                      hasEmptyData={false}
                      name="state"
                      label="استان"
                      Options={ProvincesToOption(provinces || [])}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                        // the formikSelect component will auto update the values.state but this is extra to set the default city to the first city of the newly selected state
                        setFieldValue(
                          'city',
                          findDefaultCityById(e.target.value),
                        );
                      }}
                      required
                    />
                    <FormikSelect
                      hasEmptyData={false}
                      name="city"
                      label="شهرستان"
                      Options={CitiesToOption(
                        provinces?.find(x => x.id === Number(values.state))
                          ?.cities || [],
                      )}
                      disabled={!values.state}
                    />
                  </div>
                  <CustomButton
                    type="submit"
                    title="ثبت اطلاعات"
                    isLoading={UpLoadUpdateisLoading}
                    className="bg-farm-shop-500 mb-4 h-[48px] w-full items-center rounded-[8px] justify-center text-white text-md font-Medium"
                  />
                </Form>
              )}
            </Formik>
          </div>
        )}
      </div>
      <BottomSheet
        onDismiss={() => {
          setIsOpenBottomSheet(false);
        }}
        open={isOpenBottomSheet}
        blocking={true}
        className="transition ease-in-out delay-250 "
      >
        <div className="flex flex-col w-full gap-3 p-3">
          <label
            htmlFor="editavatar"
            className="flex items-center gap-1 text-gray-900 text-md"
          >
            <SpanIcon className="text-xl icon-nameImage-2" />
            <span>{profile_image ? 'ویرایش عکس' : 'گالری'}</span>
          </label>
          <input
            onChange={event => {
              event?.target?.files &&
                dispatch(
                  addingImage({
                    imageFile: event,
                  }),
                );
              navigate('/edit/my-account/add-picture');
            }}
            className="hidden"
            type="file"
            id="editavatar"
            name="avatar"
            accept="image/png, image/jpeg"
          />
          <div
            className="flex items-center gap-1 text-red-600 text-md"
            onClick={() => {
              setIsOpenBottomSheet(false);
              setdeleteImageBottomSheet(true);
            }}
          >
            <SpanIcon className="text-xl icon-nameDelete" />
            <span>حذف عکس</span>
          </div>
        </div>
      </BottomSheet>
      <BottomSheet
        onDismiss={() => {
          setdeleteImageBottomSheet(false);
        }}
        open={deleteImageBottomSheet}
        blocking={true}
        className="transition ease-in-out delay-250 "
      >
        <div className="flex flex-col w-full gap-2 p-3">
          <div className="flex justify-between w-full">
            <p className="text-md font-Regular">حذف عکس پروفایل </p>
            <SpanIcon
              className="text-xl icon-nameClose-remove"
              onClick={() => setdeleteImageBottomSheet(false)}
            />
          </div>
          <p className="text-sm font-Regular">آیا اطمینان به حذف عکسی دارید؟</p>
          <div className="flex items-center justify-between w-full gap-2 text-md font-Medium">
            <button
              className="w-[70%] bg-red-500 h-[48px] text-white text-md rounded-[8px] font-Medium"
              onClick={() => {
                dispatch(clearAllData());
                !profile_image && setdeleteImageBottomSheet(false);
                guid && profile_image && mutate(guid);
              }}
            >
              بله، حذف
            </button>
            <button
              className="w-[30%] text-farm-shop-500 h-[48px] text-md font-Medium border border-farm-shop-500 rounded-[8px]"
              onClick={() => setdeleteImageBottomSheet(false)}
            >
              خیر
            </button>
          </div>
        </div>
      </BottomSheet>
    </div>
  );
};

export default EditMyAccountWrapper;
